import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useEffect } from "react";
import { useParams } from "react-router-dom";

import { useAppStore } from "@/store";
import { MDSPanelContainer, MDSText, MDSTextVariant } from "@/mds";
import { AudioRecordingDetailPageHeader } from "./audio-recording-detail-page-header";
import { TranscriptionSectionContent } from "@/pages/audio-recordings/components";

export const AudioRecordingDetailPage: FC = observer(() => {
  const { recordingId } = useParams<{ recordingId: string }>();
  const { store } = useAppStore();

  useEffect(() => {
    if (!recordingId) return;
    void store.audioRecordings.getAsync(recordingId);
  }, [recordingId, store.audioRecordings]);

  if (!recordingId) {
    return (
      <PageWrapper>
        <ScrollArea>
          <StatusText variant={MDSTextVariant.TextSm}>Not found.</StatusText>
        </ScrollArea>
      </PageWrapper>
    );
  }

  const recording = store.audioRecordings.get(recordingId);
  const doesNotExist = store.audioRecordings.doesNotExist(recordingId);

  if (!recording) {
    return (
      <PageWrapper>
        <ScrollArea>
          <StatusText variant={MDSTextVariant.TextSm}>
            {doesNotExist ? "Audio recording not found." : "Loading audio recording…"}
          </StatusText>
        </ScrollArea>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <HeaderSection>
        <AudioRecordingDetailPageHeader recording={recording} />
      </HeaderSection>
      <ScrollArea>
        <Card>
          <TranscriptionSectionContent recording={recording} />
        </Card>
      </ScrollArea>
    </PageWrapper>
  );
});

const PageWrapper = styled(MDSPanelContainer)`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StatusText = styled(MDSText)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 0.75rem;
  background-color: ${({ theme }) => theme.colors.surface.pane};
  margin: 1.5rem;
`;

const HeaderSection = styled.div``;
